import { cn } from '@/lib/utils';
import { PipelineStage } from '@/schema/admin';
import { Status } from '@/schema/jawsScan';
import { statusDetail } from './StatusBadge';
import { formatNumber } from '../lib/format';

/**
 * Where the work is piling up, in workflow order. Segments are proportional to the case
 * count, so a bottleneck reads as a wide band without anyone having to compare numbers.
 */
export function PipelineBar({
  stages,
  onSelect,
  activeStatus,
  className,
}: {
  stages: PipelineStage[];
  onSelect?: (status: Status) => void;
  activeStatus?: Status;
  className?: string;
}) {
  const total = stages.reduce((sum, stage) => sum + stage.count, 0);

  if (total === 0) {
    return (
      <p className={cn('rounded-2xl border border-content-highlight/35 bg-content-dark/45 p-6 text-sm text-white/50', className)}>
        No scans yet.
      </p>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex h-3 w-full overflow-hidden rounded-full border border-content-highlight/40 bg-content-dark/70">
        {stages.map((stage) => {
          const detail = statusDetail(stage.status);
          if (stage.count === 0) return null;
          return (
            <div
              key={String(stage.status)}
              className={cn('h-full', detail.className)}
              style={{ width: `${(stage.count / total) * 100}%` }}
              title={`${detail.label}: ${stage.count}`}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {stages.map((stage) => {
          const detail = statusDetail(stage.status);
          const Icon = detail.icon;
          const isActive = activeStatus === stage.status;
          const interactive = Boolean(onSelect);

          return (
            <button
              key={String(stage.status)}
              type="button"
              disabled={!interactive}
              onClick={() => onSelect?.(isActive ? null : stage.status)}
              className={cn(
                'flex flex-col gap-1 rounded-xl border p-3 text-left transition-colors',
                isActive
                  ? 'border-primary/55 bg-primary/15'
                  : 'border-content-highlight/35 bg-content-dark/45',
                interactive && 'hover:border-primary/45 hover:bg-primary/10',
                !interactive && 'cursor-default',
              )}
            >
              <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">
                <Icon className="h-3.5 w-3.5" />
                {detail.label}
              </span>
              <span className="text-xl font-semibold text-white">{formatNumber(stage.count)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
